import { isHourlyValue } from "@/offering_v2/components/date-time-picker/date_time_picker";
import { useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router";
import { TemplateName } from "../data/partner_offering";
import { OrderParams } from "./order_params";

export function useCurrentQueryString(orderParams: OrderParams) {
  const searchString = useLocation().search;

  return useMemo(() => {
    const currentSearchParams = new URLSearchParams(searchString);
    const newSearchParams = new URLSearchParams();

    currentSearchParams.delete("faq");
    currentSearchParams.delete("spaceID");
    currentSearchParams.delete("locationID");
    currentSearchParams.delete("promoCode");
    currentSearchParams.delete("startDate");
    currentSearchParams.delete("endDate");
    currentSearchParams.delete("quantity");
    currentSearchParams.delete("addOnID");
    currentSearchParams.delete("spaceOfferingID");
    currentSearchParams.delete("physicalSpaceID");
    currentSearchParams.delete("date");
    currentSearchParams.delete("time");
    currentSearchParams.delete("duration");
    currentSearchParams.delete("startTime");
    currentSearchParams.delete("endTime");
    currentSearchParams.delete("partnerOfferingLocationID");
    currentSearchParams.delete("capacity");
    currentSearchParams.delete("city");
    currentSearchParams.delete("attendees");
    currentSearchParams.delete("subscriptionStartDate");
    currentSearchParams.delete("eventTypeID");
    currentSearchParams.delete("eventTypeDescription");
    currentSearchParams.delete("layoutID");

    if (orderParams.promoCode) {
      newSearchParams.set("promoCode", orderParams.promoCode);
    }

    if (orderParams.type === TemplateName.DayPass) {
      if (orderParams.spaceID) {
        newSearchParams.set("spaceID", orderParams.spaceID);
      }
      if (orderParams.quantity) {
        newSearchParams.set("quantity", String(orderParams.quantity));
      }
      if (orderParams.dateRange?.startDate) {
        newSearchParams.set("startDate", orderParams.dateRange.startDate);
      }
      if (orderParams.dateRange?.endDate) {
        newSearchParams.set("endDate", orderParams.dateRange.endDate);
      }
    } else if (orderParams.type === "DayPassWithCapacity") {
      if (orderParams.partnerOfferingLocationID) {
        newSearchParams.set(
          "partnerOfferingLocationID",
          orderParams.partnerOfferingLocationID
        );
      }
      if (orderParams.capacity) {
        newSearchParams.set("capacity", String(orderParams.capacity));
      }
      if (orderParams.dateRange?.startDate) {
        newSearchParams.set("startDate", orderParams.dateRange.startDate);
      }
      if (orderParams.dateRange?.endDate) {
        newSearchParams.set("endDate", orderParams.dateRange.endDate);
      }
    } else if (orderParams.type === TemplateName.MultiPass) {
      if (orderParams.spaceID) {
        newSearchParams.set("spaceID", orderParams.spaceID);
      }

      if (orderParams.addOnID) {
        newSearchParams.set("addOnID", orderParams.addOnID);
      }
      if (orderParams.spaceOfferingID) {
        newSearchParams.set("spaceOfferingID", orderParams.spaceOfferingID);
      }
    } else if (orderParams.type === TemplateName.MultiDayOffice) {
      if (orderParams.partnerOfferingLocationID) {
        newSearchParams.set(
          "partnerOfferingLocationID",
          orderParams.partnerOfferingLocationID
        );
      }
      if (orderParams.capacity) {
        newSearchParams.set("capacity", String(orderParams.capacity));
      }
      if (orderParams.addOnID) {
        newSearchParams.set("addOnID", orderParams.addOnID);
      }
      if (orderParams.spaceOfferingID) {
        newSearchParams.set("spaceOfferingID", orderParams.spaceOfferingID);
      }
    } else if (orderParams.type === "MeetingRoom") {
      if (isHourlyValue(orderParams.dateRange)) {
        if (orderParams.dateRange?.date) {
          newSearchParams.set("date", orderParams.dateRange.date);
        }
        if (orderParams.dateRange?.startTime) {
          newSearchParams.set("startTime", orderParams.dateRange.startTime);
        }
        if (orderParams.dateRange?.endTime) {
          newSearchParams.set("endTime", String(orderParams.dateRange.endTime));
        }
      }

      if (!isHourlyValue(orderParams.dateRange)) {
        if (orderParams.dateRange?.startDate) {
          newSearchParams.set("startDate", orderParams.dateRange.startDate);
        }
        if (orderParams.dateRange?.endDate) {
          newSearchParams.set("endDate", orderParams.dateRange.endDate);
        }
      }
      if (orderParams.attendees) {
        newSearchParams.set("attendees", orderParams.attendees.toString());
      }
    } else if (orderParams.type === "EventSpace") {
      if (orderParams.eventTypeID) {
        newSearchParams.set("eventTypeID", orderParams.eventTypeID);
      }
      if (orderParams.eventTypeDescription) {
        newSearchParams.set(
          "eventTypeDescription",
          orderParams.eventTypeDescription
        );
      }
      if (orderParams.layoutID) {
        newSearchParams.set("layoutID", orderParams.layoutID);
      }
      if (orderParams.attendees) {
        newSearchParams.set("attendees", orderParams.attendees.toString());
      }
      if (isHourlyValue(orderParams.dateRange)) {
        if (orderParams.dateRange?.date) {
          newSearchParams.set("date", orderParams.dateRange.date);
        }
        if (orderParams.dateRange?.startTime) {
          newSearchParams.set("startTime", orderParams.dateRange.startTime);
        }
        if (orderParams.dateRange?.endTime) {
          newSearchParams.set("endTime", orderParams.dateRange.endTime);
        }
      }
      if (!isHourlyValue(orderParams.dateRange)) {
        if (orderParams.dateRange?.startDate) {
          newSearchParams.set("startDate", orderParams.dateRange.startDate);
        }
        if (orderParams.dateRange?.endDate) {
          newSearchParams.set("endDate", orderParams.dateRange.endDate);
        }
      }
    } else if (orderParams.type === "DayOffice") {
      if (orderParams.dateRange?.startDate) {
        newSearchParams.set("startDate", orderParams.dateRange.startDate);
      }
      if (orderParams.dateRange?.endDate) {
        newSearchParams.set("endDate", orderParams.dateRange.endDate);
      }
      if (orderParams.physicalSpaceID) {
        newSearchParams.set("physicalSpaceID", orderParams.physicalSpaceID);
      }
    } else if (orderParams.type === "DailyAccess") {
      if (orderParams.spaceID) {
        newSearchParams.set("spaceID", orderParams.spaceID);
      }
      if (orderParams.city) {
        newSearchParams.set("city", orderParams.city);
      }
      if (orderParams.quantity) {
        newSearchParams.set("quantity", String(orderParams.quantity));
      }
      if (orderParams.addOnID) {
        newSearchParams.set("addOnID", orderParams.addOnID);
      }
      if (orderParams.spaceOfferingID) {
        newSearchParams.set("spaceOfferingID", orderParams.spaceOfferingID);
      }
      if (orderParams.dateRange?.startDate) {
        newSearchParams.set("startDate", orderParams.dateRange.startDate);
      }
      if (orderParams.dateRange?.endDate) {
        newSearchParams.set("endDate", orderParams.dateRange.endDate);
      }
    } else if (orderParams.type === "DailyOffice") {
      if (orderParams.partnerOfferingLocationID) {
        newSearchParams.set(
          "partnerOfferingLocationID",
          orderParams.partnerOfferingLocationID
        );
      }
      if (orderParams.city) {
        newSearchParams.set("city", orderParams.city);
      }
      if (orderParams.addOnID) {
        newSearchParams.set("addOnID", orderParams.addOnID);
      }
      if (orderParams.spaceOfferingID) {
        newSearchParams.set("spaceOfferingID", orderParams.spaceOfferingID);
      }
      if (orderParams.capacity) {
        newSearchParams.set("capacity", String(orderParams.capacity));
      }
      if (orderParams.dateRange?.startDate) {
        newSearchParams.set("startDate", orderParams.dateRange.startDate);
      }
      if (orderParams.dateRange?.endDate) {
        newSearchParams.set("endDate", orderParams.dateRange.endDate);
      }
    } else if (orderParams.type === "Membership") {
      if (orderParams.locationID) {
        newSearchParams.set("locationID", orderParams.locationID);
      }
      if (orderParams.city) {
        newSearchParams.set("city", orderParams.city);
      }

      if (orderParams.subscriptionStartDate) {
        newSearchParams.set(
          "subscriptionStartDate",
          orderParams.subscriptionStartDate
        );
      }
    }

    const currentParams = currentSearchParams.toString();
    const newParams = newSearchParams.toString();

    if (currentParams) {
      return `${currentParams}${newParams ? `&${newParams}` : ""}`;
    } else {
      return newParams;
    }
  }, [orderParams, searchString]);
}

export function useCurrentURL(orderParams: OrderParams) {
  const queryString = useCurrentQueryString(orderParams);
  const { pathname: pathName } = useLocation();

  return useMemo(() => `${pathName}?${queryString}`, [queryString, pathName]);
}

export function useSyncQueryString(orderParams: OrderParams) {
  const url = useCurrentURL(orderParams);
  const history = useHistory();

  useEffect(() => {
    history.replace(url);
  }, [url, history]);
}

/**
 * This useSyncQueryStringV2 function to prevent the issue reload page
 * and scroll to the top page on any url changed by using history.replace in V1
 * @param orderParams
 */
export function useSyncQueryStringV2(orderParams: OrderParams) {
  const url = useCurrentURL(orderParams);

  useEffect(() => {
    window.history.replaceState(null, "", url);
  }, [url]);
}
